import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatDate } from "@/lib/constants";
import { Mail, Phone, Users, Sparkles, Send, Link as LinkIcon, ChevronDown, ChevronRight, Copy, FileEdit } from "lucide-react";

const TYPE_META = {
  Email:   { icon: Mail,  tone: "bg-primary/10 text-primary border-primary/20", label: "Email" },
  Call:    { icon: Phone, tone: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Call" },
  Meeting: { icon: Users, tone: "bg-blue-50 text-blue-700 border-blue-200", label: "Meeting" },
};

// LocalStorage key for the in-progress email composer draft, scoped per client.
const draftKey = (clientId) => `reliserve:comm-email-draft:${clientId}`;

// Read the persisted draft (if any) for this client.
function readDraft(clientId) {
  try {
    const raw = localStorage.getItem(draftKey(clientId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Ignore very old drafts (> 7 days) — stale intent, not worth restoring.
    if (parsed?.saved_at && Date.now() - parsed.saved_at > 7 * 24 * 3600 * 1000) return null;
    return parsed;
  } catch { return null; }
}

function writeDraft(clientId, payload) {
  try {
    localStorage.setItem(
      draftKey(clientId),
      JSON.stringify({ ...payload, saved_at: Date.now() }),
    );
  } catch { /* quota / private-mode — silently ignore */ }
}

function clearDraft(clientId) {
  try { localStorage.removeItem(draftKey(clientId)); } catch { /* noop */ }
}

// One-tap clipboard copy with a non-secure-context fallback (same pattern
// as the Risk-link ShareLinkRow).
async function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  document.execCommand("copy");
  document.body.removeChild(ta);
}

export default function CommTab({ clientId }) {
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [canAct, setCanAct] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  // When the email dialog is opened from a Call/Meeting entry, this holds the
  // link + pre-populated notes so the resulting Email entry can be attached.
  const [emailPrefill, setEmailPrefill] = useState(null);
  // Track if a saved draft exists so the composer button can show a hint.
  const [hasSavedDraft, setHasSavedDraft] = useState(false);
  useEffect(() => { setHasSavedDraft(!!readDraft(clientId)); }, [clientId, emailOpen]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/clients/${clientId}/communications`);
      setEntries(data.entries || []);
      setCanAct(!!data.can_act);
    } catch {
      toast.error("Failed to load communication timeline.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  const openEmailFromLog = async (entry) => {
    // Ask the backend to draft a summary using the logged notes.
    try {
      const { data } = await api.post(`/clients/${clientId}/communications/${entry.id}/draft-summary`);
      setEmailPrefill({
        admin_input_message: data.admin_input_message || entry.summary_notes || "",
        ai_generated_draft: data.draft || "",
        linked_from_id: entry.id,
        source_label: `Summarising ${entry.type} on ${formatDate(entry.occurred_at || entry.created_at)}`,
      });
      setEmailOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not draft summary.");
    }
  };

  return (
    <div className="space-y-6" data-testid="comm-tab">
      <Card className="flex flex-wrap items-center justify-between gap-3 border-border p-5">
        <div>
          <h3 className="font-dashboard text-base font-bold">Communication</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Every outbound email, call, and meeting for this client — in one place.
          </p>
        </div>
        {canAct && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setLogOpen(true)}
              className="gap-1"
              data-testid="comm-log-btn"
            ><Phone className="h-4 w-4" /> Log call / meeting</Button>
            <Button
              onClick={() => { setEmailPrefill(null); setEmailOpen(true); }}
              className="gap-1"
              data-testid="comm-new-email-btn"
            >
              {hasSavedDraft ? <FileEdit className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
              {hasSavedDraft ? "Resume draft" : "New email"}
            </Button>
          </div>
        )}
        {!canAct && (
          <p className="text-xs text-muted-foreground">View-only for your role.</p>
        )}
      </Card>

      <Card className="border-border p-5">
        <h4 className="mb-4 text-sm font-semibold">Timeline</h4>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : entries.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="comm-empty">
            No communication on record yet.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {entries.map((e) => (
              <TimelineRow
                key={e.id}
                entry={e}
                open={openId === e.id}
                onToggle={() => setOpenId(openId === e.id ? null : e.id)}
                onSendSummary={canAct && (e.type === "Call" || e.type === "Meeting") && !e.minutes_email_id
                  ? () => openEmailFromLog(e)
                  : null}
              />
            ))}
          </ul>
        )}
      </Card>

      <EmailComposerDialog
        open={emailOpen}
        onOpenChange={(v) => { setEmailOpen(v); if (!v) setEmailPrefill(null); }}
        clientId={clientId}
        prefill={emailPrefill}
        onSent={() => { setEmailOpen(false); setEmailPrefill(null); load(); }}
      />

      <LogDialog
        open={logOpen}
        onOpenChange={setLogOpen}
        clientId={clientId}
        onSaved={() => { setLogOpen(false); load(); }}
      />
    </div>
  );
}

// ----------------------------------------------------------------------
// Timeline row — collapsible, with plain-text body inline for Email rows.
// ----------------------------------------------------------------------
function TimelineRow({ entry, open, onToggle, onSendSummary }) {
  const meta = TYPE_META[entry.type] || TYPE_META.Email;
  const Icon = meta.icon;
  const isDiscovery = entry.source === "discovery_call";
  return (
    <li className="py-3" data-testid={`comm-row-${entry.id}`}>
      <button
        onClick={onToggle}
        className="flex w-full items-center gap-3 text-left"
        data-testid={`comm-toggle-${entry.id}`}
      >
        <span className={"inline-flex h-8 w-8 items-center justify-center rounded-md border " + meta.tone}>
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold">{meta.label}</span>
            {isDiscovery && (
              <Badge variant="secondary" className="text-[10px]" data-testid="comm-badge-discovery">
                From Discovery Call · read-only
              </Badge>
            )}
            {entry.type === "Email" && !isDiscovery && (
              <span className="text-xs text-muted-foreground">to {entry.recipient || "—"}</span>
            )}
            {(entry.type === "Call" || entry.type === "Meeting") && entry.attendees && (
              <span className="text-xs text-muted-foreground">· {entry.attendees}</span>
            )}
            {entry.minutes_email_id && (
              <Badge variant="secondary" className="text-[10px] gap-1">
                <LinkIcon className="h-3 w-3" /> Summary emailed
              </Badge>
            )}
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {formatDate(entry.occurred_at || entry.sent_at || entry.created_at)}
            {entry.subject && <> · <span className="italic">{entry.subject}</span></>}
            {isDiscovery && !entry.subject && <> · <span className="italic">{entry.subject_hint}</span></>}
          </div>
        </div>
        {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
      </button>

      {open && (
        <div className="ml-11 mt-3 space-y-3" data-testid={`comm-expanded-${entry.id}`}>
          {entry.type === "Email" ? (
            entry.final_sent_text ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    Sent body (plain text)
                  </span>
                  <CopyBodyButton text={entry.final_sent_text} rowId={entry.id} />
                </div>
                <pre
                  className="whitespace-pre-wrap break-words rounded-md border border-border bg-muted/40 p-3 font-sans text-sm leading-relaxed text-foreground/90"
                  data-testid={`comm-body-${entry.id}`}
                >{entry.final_sent_text}</pre>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">No body captured for this email.</p>
            )
          ) : (
            <>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-primary">Notes / minutes</p>
                <p
                  className="mt-1 whitespace-pre-line text-sm text-foreground/90"
                  data-testid={`comm-notes-${entry.id}`}
                >{entry.summary_notes || "—"}</p>
              </div>
              {onSendSummary && (
                <div>
                  <Button
                    size="sm"
                    onClick={onSendSummary}
                    className="gap-1"
                    data-testid={`comm-send-summary-${entry.id}`}
                  >
                    <Sparkles className="h-3.5 w-3.5" /> Send summary to client
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </li>
  );
}

// ----------------------------------------------------------------------
// Copy-body button — one-tap clipboard copy for any email body (incl. the
// Discovery ghost row). Sits next to every rendered email in the timeline.
// ----------------------------------------------------------------------
function CopyBodyButton({ text, rowId }) {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    try {
      await copyToClipboard(text);
      setCopied(true);
      toast.success("Email body copied — paste into WhatsApp");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed — select the text and copy manually");
    }
  };
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={doCopy}
      className="h-7 gap-1 px-2 text-[11px]"
      data-testid={`comm-copy-body-${rowId}`}
    >
      <Copy className="h-3 w-3" />
      {copied ? "Copied" : "Copy"}
    </Button>
  );
}

// ----------------------------------------------------------------------
// Email composer dialog — AI draft → edit → send.
// ----------------------------------------------------------------------
function EmailComposerDialog({ open, onOpenChange, clientId, prefill, onSent }) {
  const [step, setStep] = useState("input"); // "input" | "review"
  const [adminInput, setAdminInput] = useState("");
  const [draft, setDraft] = useState("");
  const [aiDraft, setAiDraft] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [linkedFromId, setLinkedFromId] = useState(null);
  const [restoredNotice, setRestoredNotice] = useState(false);

  // Open behaviour:
  //   1. Prefill from a Call/Meeting → skip straight to review with AI draft.
  //   2. No prefill + saved draft on client → restore last in-progress state.
  //   3. Fresh open with nothing → empty input step.
  useEffect(() => {
    if (!open) return;
    if (prefill) {
      setAdminInput(prefill.admin_input_message || "");
      setDraft(prefill.ai_generated_draft || "");
      setAiDraft(prefill.ai_generated_draft || "");
      setLinkedFromId(prefill.linked_from_id || null);
      setStep("review");
      setRestoredNotice(false);
      return;
    }
    const saved = readDraft(clientId);
    if (saved) {
      setAdminInput(saved.adminInput || "");
      setDraft(saved.draft || "");
      setAiDraft(saved.aiDraft || "");
      setLinkedFromId(saved.linkedFromId || null);
      setStep(saved.step || "input");
      setRestoredNotice(true);
    } else {
      setAdminInput(""); setDraft(""); setAiDraft("");
      setLinkedFromId(null); setStep("input");
      setRestoredNotice(false);
    }
  }, [open, prefill, clientId]);

  // Persist any change so switching tabs / closing the dialog doesn't lose
  // work in progress. Only persist non-prefill drafts — prefilled composers
  // are transient by design and belong to their source log entry.
  useEffect(() => {
    if (!open || prefill) return;
    const anyContent = (adminInput || "").trim() || (draft || "").trim();
    if (!anyContent) return;
    writeDraft(clientId, { adminInput, draft, aiDraft, linkedFromId, step });
  }, [open, prefill, clientId, adminInput, draft, aiDraft, linkedFromId, step]);

  const discardDraft = () => {
    clearDraft(clientId);
    setAdminInput(""); setDraft(""); setAiDraft("");
    setLinkedFromId(null); setStep("input"); setRestoredNotice(false);
    toast.success("Draft discarded");
  };

  const runDraft = async () => {
    if (!adminInput.trim()) { toast.error("Type a few lines first."); return; }
    setDrafting(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/communications/email/draft`, {
        admin_input_message: adminInput,
      });
      setDraft(data.draft || "");
      setAiDraft(data.draft || "");
      setStep("review");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Draft failed.");
    } finally { setDrafting(false); }
  };

  const send = async () => {
    if (!draft.trim()) { toast.error("Draft is empty."); return; }
    setSending(true);
    try {
      await api.post(`/clients/${clientId}/communications/email/send`, {
        admin_input_message: adminInput,
        ai_generated_draft: aiDraft,
        final_sent_text: draft,
        linked_from_id: linkedFromId,
      });
      toast.success("Email sent — logged in the timeline.");
      // Wipe any persisted draft — it's been sent, no reason to keep it.
      if (!prefill) clearDraft(clientId);
      onSent && onSent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed.");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="comm-email-dialog">
        <DialogHeader>
          <DialogTitle>{prefill ? "Send summary to client" : "New email"}</DialogTitle>
          <DialogDescription>
            {step === "input"
              ? "Type your rough message — bullets or a few lines are fine. AI will turn it into a polished, branded client email you can review before sending."
              : (prefill?.source_label
                  || "Review the AI-generated draft. Edit anything, then send it through the portal.")}
          </DialogDescription>
        </DialogHeader>

        {restoredNotice && (
          <div
            className="flex items-center justify-between gap-2 rounded-md border border-primary/30 bg-primary/5 p-2 text-xs"
            data-testid="comm-draft-restored"
          >
            <span><b>Draft restored</b> from your last session on this client.</span>
            <Button
              size="sm" variant="ghost" onClick={discardDraft}
              className="h-6 px-2 text-[11px]"
              data-testid="comm-discard-draft"
            >Discard</Button>
          </div>
        )}

        {step === "input" ? (
          <>
            <Textarea
              rows={6}
              value={adminInput}
              onChange={(e) => setAdminInput(e.target.value)}
              placeholder="e.g. remind him to send his PAN copy, and mention we're still waiting on his existing policy documents…"
              data-testid="comm-admin-input"
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Close (keeps draft)</Button>
              <Button onClick={runDraft} disabled={drafting} className="gap-1" data-testid="comm-draft-btn">
                <Sparkles className="h-4 w-4" /> {drafting ? "Drafting…" : "Draft with AI"}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="rounded-md border border-dashed border-border bg-muted/30 p-3 text-xs">
              <p className="mb-1 font-semibold text-muted-foreground">Your input</p>
              <p className="whitespace-pre-line text-foreground/80">{adminInput || "(none)"}</p>
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold text-muted-foreground">Draft — edit as needed</p>
              <Textarea
                rows={14}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                className="font-sans text-sm leading-relaxed"
                data-testid="comm-draft-body"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">
                This will be wrapped in the Reliserve Wealth branded template on send.
                We store and display the plain text — not the HTML wrapper.
                {!prefill && " Your edits are auto-saved to this client while you work."}
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep("input")}>Back</Button>
              <Button onClick={send} disabled={sending} className="gap-1" data-testid="comm-send-btn">
                <Send className="h-4 w-4" /> {sending ? "Sending…" : "Send"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ----------------------------------------------------------------------
// Log dialog — Call or Meeting.
// ----------------------------------------------------------------------
function LogDialog({ open, onOpenChange, clientId, onSaved }) {
  const [type, setType] = useState("Call");
  const [occurredAt, setOccurredAt] = useState("");
  const [attendees, setAttendees] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setType("Call"); setAttendees(""); setNotes("");
      // Default occurred_at to now (local ISO minute precision) for datetime-local input.
      const d = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      setOccurredAt(
        `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
      );
    }
  }, [open]);

  const save = async () => {
    if (!notes.trim()) { toast.error("Enter what was discussed."); return; }
    setSaving(true);
    try {
      // Convert datetime-local (naive) → ISO with local tz offset applied.
      const iso = occurredAt ? new Date(occurredAt).toISOString() : null;
      await api.post(`/clients/${clientId}/communications/log`, {
        type, occurred_at: iso, attendees, summary_notes: notes,
      });
      toast.success(`${type} logged.`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Log failed.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="comm-log-dialog">
        <DialogHeader>
          <DialogTitle>Log a call or meeting</DialogTitle>
          <DialogDescription>
            Text log only — write what was discussed. You can optionally email the client a
            summary of this afterwards from the timeline.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-muted-foreground">Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              data-testid="comm-log-type"
            >
              <option value="Call">Call</option>
              <option value="Meeting">Meeting</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-muted-foreground">When</label>
            <Input
              type="datetime-local"
              value={occurredAt}
              onChange={(e) => setOccurredAt(e.target.value)}
              data-testid="comm-log-occurred-at"
            />
          </div>
          <div className="col-span-2">
            <label className="mb-1 block text-xs font-semibold text-muted-foreground">Attendees (optional)</label>
            <Input
              value={attendees}
              onChange={(e) => setAttendees(e.target.value)}
              placeholder="e.g. Rohan, Client, Client's spouse"
              data-testid="comm-log-attendees"
            />
          </div>
          <div className="col-span-2">
            <label className="mb-1 block text-xs font-semibold text-muted-foreground">Notes / minutes</label>
            <Textarea
              rows={6}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What was discussed, decisions made, next steps agreed…"
              data-testid="comm-log-notes"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="comm-log-save">
            {saving ? "Saving…" : "Save entry"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
